import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { proxiedImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { toEmbedURL } from "@/components/PitchSchedule";

const EXTERNAL_VOTING_URL_FALLBACK = "https://www.businessshowers.org/contest";

const DAY_LABELS = {
  "2026-06-16": "Tuesday, June 16",
  "2026-06-17": "Wednesday, June 17",
  "2026-06-18": "Thursday, June 18",
};

function ReplayCard({ b, externalVotingUrl }) {
  const replay = b.replay_link || b.video_url;
  const embed = toEmbedURL(replay);
  const voteUrl = b.vote_url || externalVotingUrl;
  const businessName = b.business_name || "";

  const handleShare = async () => {
    const text = `Watch ${b.name}'s pitch (${businessName}) at the 2026 Business Showers, then cast your $2 vote.`;
    const url = `${window.location.origin}/replays?via=share#${encodeURIComponent(b.id)}`;
    if (navigator.share) {
      try { await navigator.share({ title: b.name, text, url }); return; } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Share link copied!");
    } catch {
      toast.error("Could not copy — try again.");
    }
  };

  return (
    <Card
      id={b.id}
      data-testid={`replay-card-${b.id}`}
      className="overflow-hidden border border-emerald-200 bg-white hover:border-emerald-400 transition-colors flex flex-col"
    >
      {embed ? (
        <div className="aspect-video bg-emerald-950">
          <iframe
            src={embed}
            title={`${b.name} replay`}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; encrypted-media; picture-in-picture"
            loading="lazy"
          />
        </div>
      ) : (
        <a
          href={replay || "#"}
          target="_blank"
          rel="noreferrer"
          className="block relative aspect-video bg-emerald-100 overflow-hidden group"
          data-testid={`replay-thumb-${b.id}`}
        >
          <div className="absolute inset-0 flex items-center justify-center font-[Cormorant_Garamond] text-emerald-900 text-6xl select-none" aria-hidden="true">
            {b.name?.slice(0, 1)}
          </div>
          {b.photo_url && (
            <img
              src={proxiedImage(b.photo_url)}
              alt={b.name}
              loading="lazy"
              referrerPolicy="no-referrer"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {replay && (
            <div className="absolute inset-0 bg-emerald-950/50 group-hover:bg-emerald-950/65 transition-colors flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                <div className="w-0 h-0 ml-1 border-y-[12px] border-y-transparent border-l-[20px] border-l-emerald-900" />
              </div>
            </div>
          )}
          {!replay && (
            <div className="absolute inset-x-0 bottom-0 bg-emerald-950/90 text-emerald-100 text-xs font-[Outfit] uppercase tracking-[0.2em] py-2 text-center">
              Replay coming soon
            </div>
          )}
        </a>
      )}

      <div className="p-5 flex-1 flex flex-col gap-3">
        <div>
          <div className="font-[Cormorant_Garamond] text-xl text-emerald-950 leading-tight">{b.name}</div>
          {businessName && (
            <div className="font-[Outfit] text-sm text-slate-600 mt-0.5">{businessName}</div>
          )}
          <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit] font-bold mt-2">
            {DAY_LABELS[b.pitch_day] || b.pitch_day} {b.interview_time ? `· ${b.interview_time}` : ""}
          </div>
        </div>
        {b.bio && <p className="text-sm text-slate-700 font-[Outfit] leading-relaxed line-clamp-2">{b.bio}</p>}
        <div className="flex flex-wrap gap-2 mt-auto pt-2">
          {replay && !embed && (
            <Button asChild size="sm" data-testid={`replay-watch-${b.id}`} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit] font-semibold">
              <a href={replay} target="_blank" rel="noreferrer">▶ Watch Replay</a>
            </Button>
          )}
          <Button asChild size="sm" data-testid={`replay-vote-${b.id}`} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-[Outfit] font-semibold">
            <a href={voteUrl} target="_blank" rel="noreferrer">Vote $2 →</a>
          </Button>
          <Button size="sm" variant="outline" data-testid={`replay-share-${b.id}`} onClick={handleShare} className="rounded-full border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-[Outfit]">
            Share
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function Replays() {
  const [businesses, setBusinesses] = useState([]);
  const [externalVotingUrl, setExternalVotingUrl] = useState(EXTERNAL_VOTING_URL_FALLBACK);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [cfg, sched] = await Promise.all([
          api.get("/config"),
          api.get("/pitch-schedule"),
        ]);
        setExternalVotingUrl(cfg.data?.external_voting_url || EXTERNAL_VOTING_URL_FALLBACK);
        const biz = (sched.data?.businesses || []).filter((b) => b.schedule_status !== "Tentative");
        setBusinesses(biz);
      } catch {
        toast.error("Couldn't load replays. Please refresh.");
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const order = ["2026-06-16", "2026-06-17", "2026-06-18"];
    const map = {};
    for (const d of order) map[d] = [];
    for (const b of businesses) {
      if (map[b.pitch_day]) map[b.pitch_day].push(b);
    }
    return map;
  }, [businesses]);

  const withReplay = businesses.filter((b) => b.replay_link || b.video_url).length;
  const total = businesses.length;

  return (
    <div className="min-h-screen bg-[#FFFAF0]">
      <Nav externalVotingUrl={externalVotingUrl} onScrollToForms={() => {}} onScrollToPitches={() => {}} />

      <section data-testid="replays-hero" className="bg-emerald-950 text-white py-20 sm:py-24 border-b-4 border-amber-300">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-[Outfit] font-bold">
            On Demand · June 16, 17 &amp; 18
          </div>
          <h1 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
            2026 Business Showers Replays
          </h1>
          <p className="font-[Outfit] text-emerald-100 mt-4 max-w-2xl leading-relaxed">
            Every honoree interview, on demand. Watch a pitch, then cast your $2 vote.
            Voting closes June 22 — your vote and your share are what move them forward.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button asChild className="rounded-full bg-amber-300 hover:bg-amber-400 text-emerald-950 font-[Outfit] font-semibold h-12 px-6">
              <a href={externalVotingUrl} target="_blank" rel="noreferrer">Cast Your $2 Vote →</a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-2 border-white bg-transparent text-white hover:bg-white hover:text-emerald-950 font-[Outfit] font-semibold h-12 px-6">
              <a href="/">Back to Event Page</a>
            </Button>
          </div>
          <div className="mt-6 text-xs text-emerald-200 font-[Outfit]">
            {withReplay} of {total} pitches available on demand
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Tabs value={tab} onValueChange={setTab} data-testid="replays-tabs">
            <TabsList className="bg-emerald-100 p-1">
              <TabsTrigger value="all" data-testid="replays-tab-all" className="data-[state=active]:bg-emerald-800 data-[state=active]:text-white font-[Outfit]">All</TabsTrigger>
              {["2026-06-16", "2026-06-17", "2026-06-18"].map((d) => (
                <TabsTrigger key={d} value={d} data-testid={`replays-tab-${d}`} className="data-[state=active]:bg-emerald-800 data-[state=active]:text-white font-[Outfit]">
                  {DAY_LABELS[d].replace(",", "")}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="all" className="mt-8">
              {["2026-06-16", "2026-06-17", "2026-06-18"].map((d) => (
                grouped[d].length > 0 && (
                  <div key={d} className="mb-12 last:mb-0">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-rose-600 font-[Outfit] font-bold mb-3">
                      {DAY_LABELS[d]}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {grouped[d].map((b) => (
                        <ReplayCard key={b.id} b={b} externalVotingUrl={externalVotingUrl} />
                      ))}
                    </div>
                  </div>
                )
              ))}
              {businesses.length === 0 && (
                <div className="text-center py-16 text-slate-500 font-[Outfit]">Loading replays…</div>
              )}
            </TabsContent>

            {["2026-06-16", "2026-06-17", "2026-06-18"].map((d) => (
              <TabsContent key={d} value={d} className="mt-8">
                {grouped[d].length === 0 ? (
                  <div className="text-center py-16 text-slate-500 font-[Outfit]">No honorees scheduled for this evening.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {grouped[d].map((b) => (
                      <ReplayCard key={b.id} b={b} externalVotingUrl={externalVotingUrl} />
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
}
